/**
 * Parity: working out who owns a square nobody can reach yet.
 *
 * This is the arithmetic behind the odd/even counting method, held apart from
 * the curriculum that teaches it. `lessons.ts` uses it to propose a drill; the
 * counting trainer uses it to show the working.
 *
 * The one claim made here, and the only one:
 *
 *   A square with `gaps` empty squares beneath it sits at position `gaps + 1`
 *   in its column's fill order. If that column fills one disc at a time, turn
 *   about, the player to move takes positions 1, 3, 5..., so the square is
 *   theirs exactly when `gaps` is even.
 *
 * That is arithmetic given alternating fill, and it is asserted against the
 * interpreter in `parity.test.ts` rather than taken on faith - the column is
 * really filled through `applyMove` and the seat that lands on the square is
 * compared with the count.
 *
 * What is deliberately **not** claimed here is that the owner of a threat
 * square wins the game. The popular shorthand - "odd rows belong to player one"
 * - was measured against this project's own exact solver at 83.6%, which is a
 * heuristic and is stated as one. `rowRuleOwner` exists so the trainer can show
 * the shorthand *and* show where it parts company with the count.
 *
 * Fenced to two seats and a gravity floor: alternating turns is what makes the
 * count mean anything, and `rowZero: 'top'` would invert every statement in
 * this file.
 *
 * Purity: no I/O, no clock of its own, seeded randomness.
 */
import { applyMove, createInitial, indexOf, legalMoves } from '../spec/interpreter.ts';
import type { SeatOf, State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { enumerateWinWindows } from './eval.ts';
import { winningMovesFor } from './reasons.ts';

/**
 * Whether the count means anything on this board.
 *
 * Two seats, because "the other player" has to be a single person for turns to
 * alternate; a gravity floor, because every statement here is measured upwards
 * from the bottom of a column.
 */
export function parityApplies(rules: RulesSpec): boolean {
  return rules.seats.length === 2 && rules.board.rowZero === 'bottom';
}

/** The seat that is not `seat`. Null unless the spec is a two-seat one. */
export function opponentOf(rules: RulesSpec, seat: number): number | null {
  if (rules.seats.length !== 2) return null;
  return rules.seats.find((candidate) => candidate !== seat) ?? null;
}

/** How many discs are already in a column. The floor every count starts from. */
export function columnHeight<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  col: number,
): number {
  let height = 0;
  for (let row = 0; row < rules.board.rows; row += 1) {
    if (state.board[indexOf(rules, row, col)] !== rules.cells.empty) height += 1;
  }
  return height;
}

/**
 * Empty squares between the top of a column and `cell`.
 *
 * This is the number the whole method is about, so it is returned rather than
 * folded into a verdict: the trainer asks the player for it before it says
 * anything else.
 *
 * -1 when the square is already occupied, which is not a question about the
 * future and is not something the count has an answer for.
 */
export function gapsBeneath<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  cell: number,
): number {
  if (state.board[cell] !== rules.cells.empty) return -1;
  const col = cell % rules.board.cols;
  const row = Math.floor(cell / rules.board.cols);
  return row - columnHeight(rules, state, col);
}

/**
 * The seat the count gives a square to.
 *
 * Null when the square is occupied or the spec is one the count says nothing
 * about. Note this is a statement about *that column filling alternately* - a
 * move played anywhere else passes the turn and hands the square to the other
 * player, which is the tempo half of the method and is why the trainer makes
 * you watch it happen.
 */
export function ownerOf<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  cell: number,
): number | null {
  if (!parityApplies(rules) || state.status.kind !== 'in_play') return null;
  const gaps = gapsBeneath(rules, state, cell);
  if (gaps < 0) return null;
  const mover = state.toMove as number;
  return gaps % 2 === 0 ? mover : opponentOf(rules, mover);
}

/**
 * Whether the square falls to the player to move.
 *
 * The predicate form of `ownerOf`, and the shape the parity lesson has always
 * asked its question in. False for an occupied square: already playable or
 * already buried is not a question about the future.
 */
export function fallsToMover<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  cell: number,
): boolean {
  const gaps = gapsBeneath(rules, state, cell);
  if (gaps < 0) return false;
  return gaps % 2 === 0;
}

/**
 * The rule this method is usually taught as, kept separate from the count.
 *
 * "Odd rows belong to player one, even rows to player two." It is the count's
 * special case - an untouched column with player one to move - and it is what
 * every popular guide states as a flat rule. Measured against this project's
 * exact solver it held in 83.6% of the positions it was checked on, so the
 * trainer shows it beside `ownerOf` and lets the two disagree in public rather
 * than teaching the shorthand as if it were the mechanism.
 *
 * Rows are counted from one on screen, so row index 0 is "row 1" and odd.
 */
export function rowRuleOwner(rules: RulesSpec, cell: number): number | null {
  if (!parityApplies(rules)) return null;
  const row = Math.floor(cell / rules.board.cols);
  const [first, second] = rules.seats;
  if (first === undefined || second === undefined) return null;
  return row % 2 === 0 ? first : second;
}

/**
 * Squares that would complete a line for `seat`, reachable or not.
 *
 * Deliberately not `winningMovesFor`, which only reports squares playable right
 * now. Parity is about the squares nobody can reach yet: those are the ones
 * with an owner worth working out in advance.
 */
export function latentThreatCells<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  seat: number,
): readonly number[] {
  const found = new Set<number>();
  for (const window of enumerateWinWindows(rules)) {
    let mine = 0;
    let hole = -1;
    let blocked = false;
    for (const cell of window) {
      const occupant = state.board[cell];
      if (occupant === seat) mine += 1;
      else if (occupant === rules.cells.empty) {
        if (hole >= 0) {
          blocked = true;
          break;
        }
        hole = cell;
      } else {
        blocked = true;
        break;
      }
    }
    if (!blocked && hole >= 0 && mine === window.length - 1) found.add(hole);
  }
  return [...found];
}

/**
 * Columns holding an opponent winning square that the count says is theirs.
 *
 * Gaps of one are excluded deliberately: those are squares the opponent takes
 * on their very next turn, which is visible without counting anything. Parity
 * only becomes a distinct skill when the square is too far away to simply see.
 */
export function parityDangerColumns<S extends RulesSpec>(
  rules: S,
  state: State<S>,
): readonly number[] {
  if (state.status.kind !== 'in_play' || !parityApplies(rules)) return [];
  const seat = state.toMove as number;
  const opponent = opponentOf(rules, seat);
  if (opponent === null) return [];

  const danger = new Set<number>();
  for (const cell of latentThreatCells(rules, state, opponent)) {
    const gaps = gapsBeneath(rules, state, cell);
    if (gaps >= 3 && gaps % 2 === 1) danger.add(cell % rules.board.cols);
  }
  return [...danger];
}

/** One empty square, counted. */
export interface ParitySquare {
  readonly cell: number;
  readonly row: number;
  readonly col: number;
  /** Empty squares beneath it. Zero means it is playable right now. */
  readonly gaps: number;
  /** The seat the count gives it to. */
  readonly owner: number;
  /** Seats for whom landing here completes a line. Usually empty. */
  readonly threatFor: readonly number[];
}

/**
 * Every empty square on the board, counted and owned.
 *
 * The whole-board view is what turns the rule into a method: strong players
 * scan a position this way rather than counting one square at a time, and the
 * map is the only way to see that a single move elsewhere repaints half of it.
 */
export function parityMap<S extends RulesSpec>(
  rules: S,
  state: State<S>,
): readonly ParitySquare[] {
  if (!parityApplies(rules) || state.status.kind !== 'in_play') return [];
  const mover = state.toMove as number;
  const opponent = opponentOf(rules, mover);
  if (opponent === null) return [];

  const threats = new Map<number, number[]>();
  for (const seat of rules.seats) {
    for (const cell of latentThreatCells(rules, state, seat)) {
      const seats = threats.get(cell);
      if (seats) seats.push(seat);
      else threats.set(cell, [seat]);
    }
  }

  const squares: ParitySquare[] = [];
  for (let col = 0; col < rules.board.cols; col += 1) {
    const height = columnHeight(rules, state, col);
    for (let row = height; row < rules.board.rows; row += 1) {
      const gaps = row - height;
      squares.push({
        cell: indexOf(rules, row, col),
        row,
        col,
        gaps,
        owner: gaps % 2 === 0 ? mover : opponent,
        threatFor: Object.freeze(threats.get(indexOf(rules, row, col)) ?? []),
      });
    }
  }
  return squares;
}

/** One rung of a column's fill order. */
export interface LadderStep {
  readonly cell: number;
  readonly row: number;
  /** The seat that would place this disc. */
  readonly seat: number;
  /** 1 for the next disc into this column, 2 for the one after, and so on. */
  readonly ordinal: number;
}

/**
 * The column drawn as a fill order, up to and including `cell`.
 *
 * This is the proof, and it is why the trainer can afford to be brief in prose:
 * a player who has seen the discs numbered off alternately up to the square
 * does not need the parity rule restated. Empty when the square is occupied or
 * the count does not apply.
 */
export function fillLadder<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  cell: number,
): readonly LadderStep[] {
  if (!parityApplies(rules) || state.status.kind !== 'in_play') return [];
  const gaps = gapsBeneath(rules, state, cell);
  if (gaps < 0) return [];

  const col = cell % rules.board.cols;
  const height = columnHeight(rules, state, col);
  const mover = state.toMove as number;
  const opponent = opponentOf(rules, mover);
  if (opponent === null) return [];

  const steps: LadderStep[] = [];
  for (let step = 0; step <= gaps; step += 1) {
    const row = height + step;
    steps.push({
      cell: indexOf(rules, row, col),
      row,
      seat: step % 2 === 0 ? mover : opponent,
      ordinal: step + 1,
    });
  }
  return steps;
}

/** A position chosen because one square on it is worth counting. */
export interface ParityPosition {
  readonly id: string;
  readonly firstSeat: number;
  /** The moves that built the position, so it replays through the interpreter. */
  readonly setup: readonly number[];
  /** The seat to move, and the one the questions are asked of. */
  readonly seat: number;
  /** The square being counted. */
  readonly target: number;
  readonly gaps: number;
  /** The seat the count gives the target to. */
  readonly owner: number;
  /** Seats for whom the target completes a line. Never empty in a hunted position. */
  readonly threatFor: readonly number[];
}

export interface HuntOptions {
  readonly seed?: number;
  readonly budgetMs?: number;
  readonly now?: () => number;
  /**
   * Qualifying positions to walk past before accepting one. Without it the hunt
   * returns the first position it trips over, which for a fixed seed is the
   * same board every time.
   */
  readonly skip?: number;
}

const DEFAULT_HUNT_BUDGET_MS = 4000;

/**
 * How far off the target square may be.
 *
 * Below two the square is playable now or next turn, which is something you can
 * see rather than something you count. Above four the count is right but the
 * position it describes is too far away to have any grip on the game in front
 * of you.
 */
const MIN_GAPS = 2;
const MAX_GAPS = 4;

/** Deterministic PRNG. A shared `Math.random` would make positions unrepeatable. */
function seededRandom(seed: number): () => number {
  let state = (seed >>> 0) || 1;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 2 ** 32;
  };
}

/**
 * One move of a plausible game.
 *
 * The same two reflexes the lessons and puzzle hunts use - take a win in front
 * of you, block a loss in front of you, otherwise drift towards the middle -
 * kept local so this module does not depend on the curriculum that consumes it.
 * Purely random play produces boards no human would reach, and a counting
 * exercise set on one teaches a pattern that will never come up.
 */
function walk<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  random: () => number,
): { readonly param: number } | undefined {
  const moves = legalMoves(rules, state);
  if (moves.length === 0) return undefined;

  const seat = state.toMove;
  const mine = winningMovesFor(rules, state, seat);
  const takeIt = mine[0];
  if (takeIt !== undefined) return { param: takeIt };

  const opponent = opponentOf(rules, seat as number);
  if (opponent !== null) {
    const theirs = winningMovesFor(rules, state, opponent as SeatOf<S>);
    const block = theirs[0];
    if (theirs.length === 1 && block !== undefined) return { param: block };
  }

  const middle = (rules.board.cols - 1) / 2;
  const weights = moves.map((move) => 1 / (1 + Math.abs(move.param - middle)));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (total <= 0) return moves[0];

  let ticket = random() * total;
  for (const [index, weight] of weights.entries()) {
    ticket -= weight;
    if (ticket <= 0) return moves[index];
  }
  return moves[moves.length - 1];
}

/**
 * Squares on this board that are worth counting.
 *
 * A latent threat for either side, far enough up that the answer has to be
 * counted rather than seen. Both sides' squares qualify: knowing a square is
 * *yours* is what tells you to fill under it, and a trainer that only ever
 * showed the opponent's would teach half the method.
 */
export function countableSquares<S extends RulesSpec>(
  rules: S,
  state: State<S>,
): readonly ParitySquare[] {
  return parityMap(rules, state).filter(
    (square) => square.threatFor.length > 0 && square.gaps >= MIN_GAPS && square.gaps <= MAX_GAPS,
  );
}

/**
 * Hunt for a position with a square worth counting.
 *
 * Cheap on purpose, and this is the difference between this and the parity
 * *lesson*. The lesson has to prove with exact search that filling a column
 * loses, which takes about eleven seconds; the trainer only ever states the
 * count, which is arithmetic, so it needs no search at all and a position
 * arrives in milliseconds.
 *
 * Returns null when the budget runs out rather than overrunning.
 */
export function findParityPosition<S extends RulesSpec>(
  rules: S,
  options: HuntOptions = {},
): ParityPosition | null {
  if (!parityApplies(rules)) return null;

  const now = options.now ?? (() => Date.now());
  const deadline = now() + (options.budgetMs ?? DEFAULT_HUNT_BUDGET_MS);
  const random = seededRandom(options.seed ?? 1);
  const firstSeat = rules.seats[0] ?? 1;
  let toSkip = options.skip ?? Math.floor(random() * 8);

  /** The first one found, so a hunt that runs out while skipping still answers. */
  let fallback: ParityPosition | null = null;

  while (now() < deadline) {
    let state: State<S> = createInitial(rules, firstSeat as SeatOf<S>);
    const setup: number[] = [];

    while (state.status.kind === 'in_play') {
      if (now() >= deadline) return fallback;

      const found = pick(rules, state, firstSeat, setup, random);
      if (found !== null) {
        if (toSkip <= 0) return found;
        fallback ??= found;
        toSkip -= 1;
      }

      const chosen = walk(rules, state, random);
      if (!chosen) break;
      const applied = applyMove(rules, state, state.toMove, chosen);
      if (!applied.ok) break;
      setup.push(chosen.param);
      state = applied.state;
    }
  }

  return fallback;
}

/**
 * Whether this position asks a counting question, and which square it asks it
 * about.
 *
 * A position where somebody can win right now is not one of them: the answer
 * there is "take it" or "block it", and counting a square four rows up is noise
 * beside a line that finishes this turn.
 */
function pick<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  firstSeat: number,
  setup: readonly number[],
  random: () => number,
): ParityPosition | null {
  if (state.status.kind !== 'in_play') return null;
  if (legalMoves(rules, state).length < 3) return null;

  const seat = state.toMove as number;
  const opponent = opponentOf(rules, seat);
  if (opponent === null) return null;
  if (winningMovesFor(rules, state, seat as SeatOf<S>).length > 0) return null;
  if (winningMovesFor(rules, state, opponent as SeatOf<S>).length > 0) return null;

  const candidates = countableSquares(rules, state);
  const target = candidates[Math.floor(random() * candidates.length)];
  if (target === undefined) return null;

  return Object.freeze({
    id: `parity:${String(firstSeat)}:${setup.join('')}:${String(target.cell)}`,
    firstSeat,
    setup: Object.freeze([...setup]),
    seat,
    target: target.cell,
    gaps: target.gaps,
    owner: target.owner,
    threatFor: target.threatFor,
  });
}

/** Replay a hunted position. Null when the moves do not describe a live board. */
export function replayParity<S extends RulesSpec>(
  rules: S,
  firstSeat: number,
  setup: readonly number[],
): State<S> | null {
  let state = createInitial(rules, firstSeat as SeatOf<S>);
  for (const param of setup) {
    if (state.status.kind !== 'in_play') return null;
    const applied = applyMove(rules, state, state.toMove, { param });
    if (!applied.ok) return null;
    state = applied.state;
  }
  return state.status.kind === 'in_play' ? state : null;
}
